// Utils for working with map features
import { isProxy } from 'vue'
import geobuf from 'geobuf'
import Pbf from 'pbf'

import bboxPolygon from '@turf/bbox-polygon'
import booleanPointInPolygon from '@turf/boolean-point-in-polygon'
import centroid from '@turf/centroid'
import distance from '@turf/distance'
import { point } from '@turf/helpers'
import type { AllGeoJSON, Coord } from '@turf/helpers'
import type { BBox, Feature, GeoJsonProperties } from 'geojson'

import head from 'lodash/head'
import last from 'lodash/last'
import uniqBy from 'lodash/uniqBy'

import type { GeoJSONFeature, MapboxMap } from '@/lib/map-x/webmap-viewer-types'

import Camera from '@/lib/camera'
import { ASPECT_BEARING } from '@/lib/enums'
import { isPrivateFeature } from '@/lib/feature-identification'
import Geographic from '@/lib/geographic'
import MapFeature from '@/lib/map-feature'

import {
  camera,
  setDestination,
  setFitBounds,
  setFitFeature,
  setTrailMarkerFeatures,
} from '@/services/map-service'

import {
  isWaypoint,
  isShape,
  isLine,
  isRoute,
  isTrack,
} from '@/services/markup-service/helpers'

import type { Markup } from '@/types/markups'

import { kilometersToMeters } from '@/components/shared/x-distance.vue'

// The property found in a feature that contains the adventure id (if there is one)
export const ADVENTURE_ID_PROPERTY = 'data:adventure_id'

// The property found in a non-rich content feature that contains the adventure id
export const NON_RICH_ID_PROPERTY = 'id'

// A property on the adventure layer which contains the bounding box of the
// entire adventure
export const BBOX_CALCULATED = 'data:bbox'

// The property in a feature where the static id can be found
export const FEATURE_STATIC_ID_PROPERTY = 'data:static_id'

// Property for the rich content adventure (Backcountry)
export const ADVENTURE_LINE_LAYER = 'rich_content_adventure_line'

export const ADVENTURE_ROUTE_LAYER = 'rich_content_route'

// Property for the padding to use when querying map features around a point
export const FEATURE_DETECTION_PADDING = 5

const MAX_DISTANCE_KM = 300

/** FEATURE ZOOM LEVEL CONSTANTS */
// Camera heights at which various map features become visible
export const FEATURED_TRAILS_VISIBLE_HEIGHT = 8
export const SNOTEL_VISIBLE_HEIGHT = 7

/**
 * Returns a new sorted list of features with private parcels first
 * @param features - a list of features to sort from map tiles
 * @returns a sorted list of features with private parcels first
 */
export function sortFeaturesPrivateFirst(features: MapFeature[]): MapFeature[] {
  return [...features].sort((a, b) => {
    const aOrder = isPrivateFeature(a) ? 0 : 1
    const bOrder = isPrivateFeature(b) ? 0 : 1
    return aOrder - bOrder
  })
}

/**
 * Returns the first Bbox for any of the features in the array. Currently, adventure
 * layers is the only layer that supports a bbox.
 */
export const getMapFeaturesBbox = (
  feature: MapFeature | null | undefined
): string | null => {
  if (feature) {
    // Currently, the only features that can have bboxes are adventure labels
    return getAdventureLabelBbox(feature)
  }

  return null
}

/**
 * Determines if a feature is a base collision box (not an interaction state collision box).
 *
 * Returns false for layers ending with 'selected', 'hovered', or 'targeted' which are larger collision boxes.
 * Returns true for base collision boxes or non-collision box layers.
 *
 * @param feature - Mapbox feature (raw or MapFeature)
 * @returns true if feature is a base collision box, false if it's an interaction state collision box
 */
export const isBaseCollisionBox = (feature: {
  layer?: { id?: string } | string | null
  sourceLayer?: string
}): boolean => {
  const layerObj = feature.layer
  const layerId = (typeof layerObj === 'object' ? layerObj?.id : layerObj) ?? ''
  const sourceLayer = feature.sourceLayer || ''

  // Only check collision boxes for rec_point and places source layers
  const isRecPointOrPlaces =
    sourceLayer === 'rec_point' || sourceLayer === 'places'
  if (!isRecPointOrPlaces) {
    return true
  }

  // Check if layer ID ends with interaction state indicators
  const interactionStateIndicators = ['selected', 'hovered', 'targeted']
  const hasInteractionState = interactionStateIndicators.some((indicator) =>
    layerId.endsWith(indicator)
  )

  // Return true for base collision boxes (no interaction state)
  return !hasInteractionState
}

/**
 * Returns the features that are rendered in the map at the given point with padding.
 *
 * @param map - Mapbox instance
 * @param pixelPoint - point to query for rendered features
 * @param padding - padding to apply to the points search area
 * @param useBaseCollisionBox - whether to use only base collision boxes, filtering out enhanced ones (default: false)
 * @returns array of features
 */
export const getMapFeatures = (
  map: MapboxMap,
  pixelPoint: { x: number; y: number },
  padding: number,
  useBaseCollisionBox = false
): MapFeature[] => {
  const bbox: [[number, number], [number, number]] = [
    [pixelPoint.x - padding, pixelPoint.y - padding],
    [pixelPoint.x + padding, pixelPoint.y + padding],
  ]
  let features: GeoJSONFeature[] = []
  try {
    features = map.queryRenderedFeatures(bbox)
  } catch (e) {
    // DEM tile out-of-range — safe to ignore, return empty
    if (!(e instanceof RangeError)) {
      throw e
    }
  }
  const outputFeatures: MapFeature[] = []

  features.forEach((feature) => {
    if (feature.properties?.id) {
      // Filter out non-base collision boxes if using base collision box only
      if (useBaseCollisionBox && !isBaseCollisionBox(feature)) {
        return
      }

      // only return features with tile properties
      const mapFeature = MapFeature.fromMapbox(feature)
      outputFeatures.push(mapFeature)
    }
  })

  const outputFeaturesUniq = uniqBy(outputFeatures, (f) =>
    [f.layer, f.id].join()
  )
  return outputFeaturesUniq
}

/**
 * Returns the first bbox of an adventure layer feature (if there is one).
 */
export const getAdventureLabelBbox = (
  adventureFeature: MapFeature | null | undefined
): string | null => {
  // Check to see if any of the features are adventure label features.
  // We can check this by seeing if they are on the adventure area label.
  // If the feature is an adventure, we want to return the Bbox
  if (adventureFeature) {
    // The feature has the bbox as a string, we need to parse it
    // Check both property names for backwards compatibility
    const bboxValue =
      adventureFeature?.properties?.['bbox_calculated'] ??
      adventureFeature?.properties?.[BBOX_CALCULATED]
    if (bboxValue) {
      // The bbox from the properties is in the form of [[x,y],[x,y]]
      // Where the first coordinate is the lower left and the second is
      // the top right of the bbox.  The geoJSON spec expects these
      // to be in a single array, not an array of arrays. We want to
      // pass in an adventure bbox in the geoJSON standard.
      const parsedBbox = JSON.parse(bboxValue)
      return parsedBbox[0].concat(parsedBbox[1]).join(',')
    }
  }

  return null
}

/**
 * Takes the features passed in and returns the first one that is on the adventure
 * area label layer.
 * @param features - array of features
 * @returns the first adventure area feature
 */
export const getAdventureAreaFeature = (
  features: MapFeature[]
): MapFeature | null => {
  const adventureFeatures = features.filter(isAdventureLabelFeature)
  if (adventureFeatures.length > 0) {
    // If we have multiple adventure labels "stacked" and we click and get multiple
    // adventure features, we want to return the first one for now.
    return adventureFeatures[0]
  }

  return null
}

const ADVENTURE_AREA_LABEL = 'adventure_area_label'
const AVENTURE_AREA_BIKE_LABEL = 'adventure_area_bike_label'

/**
 * Returns a boolean indicating if the feature is an adventure area label.
 * @param feature - feature from map
 */
const isAdventureLabelFeature = (feature: MapFeature): boolean => {
  const { layer } = feature
  return layer === ADVENTURE_AREA_LABEL || layer === AVENTURE_AREA_BIKE_LABEL
}

/**
 * Function that will take an array of features and search them to find the feature
 * that matches the adventure id on the ADVENTURE_LINE_LAYER layer or the static_id from the tiles.
 *
 * @param id - The id of the adventure we are looking for
 * @param features - Array of map features that we want to search for
 * @param app - Offroad or Backcountry
 */
export const getFeatureById = (
  id: string | undefined,
  features: MapFeature[] | null | undefined,
  app: string
): MapFeature | undefined => {
  const offroadLayers = [
    ADVENTURE_ROUTE_LAYER,
    'sxs',
    'sixty_inch',
    'atv',
    'overland',
    'dirt_bike',
  ]
  const backcountryLayers = [ADVENTURE_LINE_LAYER]
  const sourceLayerArr = app === 'offroad' ? offroadLayers : backcountryLayers
  if (features) {
    return features.find(
      (q) =>
        (q.properties?.[ADVENTURE_ID_PROPERTY] === id ||
          q.properties?.[FEATURE_STATIC_ID_PROPERTY] === id ||
          q.properties?.[NON_RICH_ID_PROPERTY] === id) &&
        (sourceLayerArr.includes(q.layer) ||
          q.layer?.startsWith('offroad-trail:'))
    )
  }
  return undefined
}

/**
 * Finds an adventure area label feature by id, searching only the area label layers.
 * Use this instead of getFeatureById when looking up area label features, to avoid
 * accidentally matching line features that other callers expect.
 *
 * @param id - The id of the adventure area label we are looking for
 * @param features - Array of map features to search
 * @returns
 */
export const getAreaLabelFeatureById = (
  id: string | undefined,
  features: MapFeature[] | null | undefined
): MapFeature | undefined => {
  const areaLabelLayers = [ADVENTURE_AREA_LABEL, AVENTURE_AREA_BIKE_LABEL]
  return features?.find(
    (q: MapFeature) =>
      (q.properties?.[ADVENTURE_ID_PROPERTY] === id ||
        q.properties?.[FEATURE_STATIC_ID_PROPERTY] === id ||
        q.properties?.[NON_RICH_ID_PROPERTY] === id) &&
      areaLabelLayers.includes(q.layer)
  )
}

/**
 * Finds the first feature whose identifier list intersects with the provided identifiers.
 *
 * @param identifiers - Identifiers associated with a destination
 * @param features - Features to search
 * @param featureLayerMatches - Optional filter to ensure feature is on the expected layer
 */
export const getFeatureByIdentifiers = (
  identifiers: string[] | undefined | null,
  features: MapFeature[] | undefined | null,
  featureLayerMatches?: (feature: MapFeature) => boolean
): MapFeature | null => {
  if (!identifiers?.length || !features?.length) {
    return null
  }

  const identifierSet = new Set(identifiers)

  return (
    features.find((feature) => {
      if (featureLayerMatches && !featureLayerMatches(feature)) {
        return false
      }

      const idString = feature.properties?.['data:onx:identifiers']
      if (typeof idString !== 'string') {
        return false
      }

      return idString
        .split(';')
        .some((identifier) => identifierSet.has(identifier))
    }) ?? null
  )
}

/**
 * Takes in a bbox and a point and returns a boolean indicating if the point is inside the box
 *
 * @param bbox - GeoJSON bounding box
 * @param turfPoint - Turf geoJSON point
 * @returns Boolean indicating if the point is inside the bounding box
 */
export const isPointInBoundingBox = (
  bbox: BBox,
  turfPoint: Feature<GeoJSON.Point, GeoJsonProperties>
): boolean => {
  // Use turf to convert the bbox to a polygon
  const polygon = bboxPolygon(bbox)
  return booleanPointInPolygon(turfPoint, polygon)
}

/**
 * Function that gets the static feature id for the feature passed in.
 *
 * @param adventureFeature - the map feature we are attempting to get the static id for
 * @returns The static id of the feature
 */
export const getFeatureStaticId = (
  adventureFeature: MapFeature | null | undefined
): string | null => {
  return adventureFeature && adventureFeature.properties
    ? adventureFeature.properties[FEATURE_STATIC_ID_PROPERTY]
    : null
}

/**
 * Function that sets map trail markers on an adventure trail map feature
 * @param adventureFeature - the map feature we are attempting to add trail markers to
 */
export const parseAndShowTrailMarkers = (
  adventureFeature: MapFeature
): void => {
  const { geometry } = adventureFeature
  let start: number[] | undefined
  let end: number[] | undefined
  if (geometry?.type === 'LineString') {
    start = head(geometry.coordinates)
    end = last(geometry.coordinates)
  } else if (geometry?.type === 'MultiLineString') {
    if (geometry.coordinates.length === 1) {
      start = head(geometry.coordinates[0])
      end = last(geometry.coordinates[0])
    }
  }
  if (start && end) {
    const features = []
    const distanceMeters = kilometersToMeters(distance(start, end))
    const MAX_LOOP_DISTANCE_METERS = 20
    if (distanceMeters < MAX_LOOP_DISTANCE_METERS) {
      features.push(point(start, { icon: 'trail-loop' }))
    } else {
      features.push(point(start, { icon: 'trail-start' }))
      features.push(point(end, { icon: 'trail-end' }))
    }
    setTrailMarkerFeatures(...features)
  }
}

/**
 * Take a flat bounding box array, and returns the format necessary for Mapbox fitBounds()
 * @param bbox Array of 4 points as a bounding box [lng, lat, lng, lat]
 * @returns Nested array needed to use Mapbox fitBounds()
 */
export const formatBboxtoFitBounds = (
  bbox: [number, number, number, number]
): [[number, number], [number, number]] => [
  [bbox[0], bbox[1]],
  [bbox[2], bbox[3]],
]

/**
 * Is a destination point more than 300km away from our current camera location?
 * @param destinationPoint - Point object (Geographic, Turf, potentially Proxy)
 * @returns Is the point further than the max or not
 */
export const maxDistancePoints = (
  destinationPoint: Geographic | AllGeoJSON | unknown
): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { location } = camera()!
  let resolved = destinationPoint
  // This case only comes up from the "get user location" button
  // We get a returned Proxy of a Camera object, which we need to set to a Geographic
  if (isProxy(resolved)) {
    const { longitude, latitude, height } = resolved as {
      longitude: number
      latitude: number
      height: number
    }
    resolved = new Geographic(longitude, latitude, height)
  }

  // If our destination is an instance of a Geographic, convert it to a Turf point
  // Otherwise we're already working with a Turf point
  let formattedDestination: Coord
  if (resolved instanceof Geographic) {
    formattedDestination = resolved.toTurfPoint()
  } else {
    formattedDestination = resolved as Coord
  }
  const formattedLocation = location.toTurfPoint()

  const distanceBetweenPoints = distance(
    formattedLocation,
    formattedDestination
  )
  return distanceBetweenPoints > MAX_DISTANCE_KM
}

/**
 * Is a destination feature more than 300km away from our current camera location?
 * @param destinationFeature - Geojson feature
 * @returns Is the feature further than the max or not
 */
export const maxDistancePointsFeature = (
  destinationFeature: AllGeoJSON
): boolean => {
  const centroidPoint = centroid(destinationFeature)
  return maxDistancePoints(centroidPoint)
}

/**
 * Is a destination bbox more than 300km away from our current camera location?
 * @param destinationBbox - Bounding box of a feature
 * @returns Is the feature further than the max or not
 */
export const maxDistancePointsBbox = (destinationBbox: number[][]): boolean => {
  const firstPoint = destinationBbox[0]
  const turfPoint = point(firstPoint)
  return maxDistancePoints(turfPoint)
}

interface AnimationOptions {
  feature: Feature
  maxZoom?: number
  minZoom?: number
  pitch?: number
  aspects?: string[]
  preserveOrientation?: boolean
}

/**
 * Animate the map to a map feature, with optional configurations for zoom, pitch and bearing
 *
 * @param animationOptions
 */
export function animateToFeatureBounds({
  feature,
  maxZoom = 11,
  minZoom,
  pitch = 0,
  aspects = [],
  preserveOrientation = false,
}: AnimationOptions): void {
  let bearing = 0
  let finalPitch = pitch

  if (preserveOrientation) {
    bearing = camera()?.bearing ?? bearing
    finalPitch = camera()?.pitch ?? finalPitch
  } else if (aspects && aspects.length) {
    // Set the middle aspect in the array as our primary aspect
    const primaryAspect = aspects[Math.floor(aspects.length / 2)]
    // If it's "ALL", set the angle at 0, else get the prescribed angle
    let bearingForFeature = 0
    if (primaryAspect !== 'ALL' && primaryAspect in ASPECT_BEARING) {
      bearingForFeature =
        ASPECT_BEARING[primaryAspect as keyof typeof ASPECT_BEARING].camera
    }
    bearing = bearingForFeature
  }

  setFitFeature({
    feature,
    maxZoom,
    pitch: finalPitch,
    bearing,
    minZoom,
    padding: { top: 100, bottom: 100, left: 100, right: 100 },
  })
}

/**
 * Accepts a 2d or 3d Bbox and returns a 2d Bbox
 *
 * @throws if length is shorter than needed for 2d BBox
 * @param bbox
 * @returns 2d BBox
 */
export const toBBox2d = (bbox: number[]): [number, number, number, number] => {
  if (bbox.length < 4) {
    throw Error('Invalid BBox')
  }
  const [a, b, c, d] = bbox
  return [a, b, c, d]
}

/**
 * Pan to a markup feature
 * @param markup - The markup viewmodel to pan to
 * @param zoom - Optional zoom level to set when panning to the markup, defaults to 14
 */
export const panToMarkup = (
  markup: Markup | null | undefined,
  zoom = 14
): void => {
  if (!markup) {
    return
  }

  // Handle waypoints (point-based)
  if (isWaypoint(markup) && markup.location) {
    // Package waypoints have location as [lon, lat] array
    const [lng, lat] = markup.location as unknown as number[]

    setDestination(new Camera({ location: new Geographic(lng, lat, zoom) }))
  }

  // Handle shapes, lines, routes, tracks (bbox-based)
  if (
    (isShape(markup) || isLine(markup) || isRoute(markup) || isTrack(markup)) &&
    markup.bbox
  ) {
    const formattedBbox = formatBboxtoFitBounds(toBBox2d(markup.bbox))

    if (formattedBbox) {
      setFitBounds(formattedBbox)
    }
  }
}

/**
 * Determines if a given GeoJson feature is a Point or MultiPoint
 * @param feature - GeoJson feature
 */
export const isPointFeature = (
  feature: AllGeoJSON | null | undefined
): boolean => {
  return ['Point', 'MultiPoint'].includes((feature as Feature)?.geometry?.type)
}

/**
 * Take geobuf string and transform to geojson object
 */
export function convertGeobufToJSON(geobufString: string): object {
  // Convert base64 string to Uint8Array
  const buffer = Uint8Array.from(window?.atob(geobufString), (c) =>
    c.charCodeAt(0)
  )
  // Use mapbox features to decode protocol buffer, resulting in geojson
  const geojson = geobuf.decode(new Pbf(buffer))
  return geojson
}
